import { toast } from "sonner"

import { AppShell } from "@/components/app-shell"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { IconFileInvoice, IconNetwork } from "@tabler/icons-react"

interface Invoice {
  id: number
  amount_cents: number
  amount: number
  formatted_amount: string
  currency: string
  status: string
  description: string | null
  paid_at: string | null
  created_at: string
  pdf_url: string | null
}

interface BillingIndexProps {
  invoices: Invoice[]
}

function getStatusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "paid":
      return "default"
    case "pending":
      return "secondary"
    case "failed":
      return "destructive"
    case "refunded":
      return "outline"
    default:
      return "secondary"
  }
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

export default function BillingIndex({ invoices }: BillingIndexProps) {
  const handleNotImplemented = (action: string) => {
    toast.info(`${action} is not implemented yet`)
  }

  return (
    <AppShell title="Billing">
      <div className="px-4 lg:px-6 space-y-6">
        <Card>
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-4">
              <IconNetwork className="size-10 text-muted-foreground" />
              <div>
                <h3 className="font-semibold">Pro plan</h3>
                <p className="text-sm text-muted-foreground">Annual</p>
                <p className="text-sm text-muted-foreground">
                  Your subscription will be canceled on Feb 27, 2026.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => handleNotImplemented("Adjust plan")}
            >
              Adjust plan
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Payment</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Subscription canceled</p>
              <p className="text-sm text-muted-foreground">
                Plan ends on Feb 27, 2026
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => handleNotImplemented("Resubscribe")}
            >
              Resubscribe
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <IconFileInvoice className="size-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No invoices yet</h3>
                <p className="text-sm text-muted-foreground">
                  Your billing history will appear here
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell>{formatDate(invoice.created_at)}</TableCell>
                      <TableCell className="font-medium">
                        ${invoice.amount.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusVariant(invoice.status)}>
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {invoice.pdf_url ? (
                          <Button variant="outline" size="sm" asChild>
                            <a href={invoice.pdf_url} target="_blank" rel="noopener noreferrer">
                              View
                            </a>
                          </Button>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Cancellation</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between">
            <p className="text-sm">Cancel plan</p>
            <Button
              variant="destructive"
              onClick={() => handleNotImplemented("Cancel subscription")}
            >
              Cancel
            </Button>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  )
}
